import React, { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { IQuarter } from '../../../../../app/resource/annuals/annuals.types';
import { annualsSelectors } from '../../../../../app/resource/annuals/annuals.selectors';
import { useSelect } from '../../../../../utils/selector.utils';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { IPeriodAndCustomDateRangeFilters } from './types';
import { useApiRequestState } from '../../../../../utils/api.utils';

const PeriodAndCustomDateRangeFilters: FunctionComponent<IPeriodAndCustomDateRangeFilters> = ({
  callback,
  pageInProgressKey,
}) => {
  const today = new Date();
  const [dateRange, setDateRange] = useState<[Date, Date] | [null, null]>([new Date(today.getFullYear(), 0, 1), today]);
  const [currentQuarterId, setCurrentQuarterId] = useState<IQuarter['id'] | undefined>();
  const [userHasSelectedDateRange, setUserHasSelectedDateRange] = useState(false);

  const annuals = useSelect(annualsSelectors.selectResourceList);
  const quarters = useMemo(() => annuals.flatMap((annual) => annual.quarters), [annuals]);
  const currentIndex = useMemo(() => {
    if (!currentQuarterId) {
      const today = new Date();
      return quarters.findIndex((quarter) => {
        const startDate = new Date(quarter.startDate);
        const endDate = new Date(quarter.endDate);
        return today >= startDate && today <= endDate;
      });
    } else {
      return quarters.findIndex((quarter) => {
        return quarter.id === currentQuarterId;
      });
    }
  }, [quarters, currentQuarterId]);
  const previousQuarter = currentIndex > 0 ? quarters[currentIndex - 1] : undefined;
  const nextQuarter = currentIndex < quarters.length - 1 ? quarters[currentIndex + 1] : undefined;
  const currentQuarter = quarters.find((quarter) => quarter.id === currentQuarterId);
  const currentAnnual = annuals.find((annual) => annual.quarters.find((q) => q.id === currentQuarterId));

  const [showLoader] = useApiRequestState(pageInProgressKey);

  const [startDate, endDate] = dateRange;

  const datePlaceholder =
    !startDate && !endDate
      ? 'Select date range...'
      : `${startDate?.toLocaleDateString()} - ${endDate !== null ? endDate?.toLocaleDateString() : ''}`;

  useEffect(() => {
    if (userHasSelectedDateRange && startDate && endDate) {
      callback({ dateSince: startDate, dateUntil: endDate });
    }
  }, [startDate, endDate, callback, userHasSelectedDateRange]);

  const handleRemove = () => {
    setDateRange([null, null]);
    setCurrentQuarterId(undefined);
    callback();
  };

  const fetchQuarter = (quarter: IQuarter) => {
    if (quarter.id !== currentQuarterId) {
      setCurrentQuarterId(quarter.id);
      setDateRange([null, null]);
      callback({ quarterId: quarter.id });
    }
  };

  return (
    <div className="flex h100">
      {annuals.length > 0 && (
        <>
          {currentAnnual && currentQuarter && (
            <span
              className="flex align-center justify-center white border-radius-1 mr-1 yellow-bg"
              style={{ padding: 5, width: 130, height: '100%' }}
            >
              {`${currentAnnual.year} - Period ${currentQuarter.number}`}
            </span>
          )}
          <a
            title="Previous Quarter"
            className={`btn btn-md ml-1 btn-border white-bg ${previousQuarter ? '' : 'disabled-link'}`}
            style={{ height: '100%' }}
            onClick={previousQuarter ? () => fetchQuarter(previousQuarter) : undefined}
          >
            <IoIosArrowBack />
            Previous Quarter
          </a>
          <a
            className={`btn btn-md btn-border white-bg mr-0 ${nextQuarter ? '' : 'disabled-link'}`}
            title="Next Quarter"
            style={{ height: '100%' }}
            onClick={nextQuarter ? () => fetchQuarter(nextQuarter) : undefined}
          >
            Next Quarter
            <IoIosArrowForward />
          </a>
        </>
      )}
      <div className="date-range-picker">
        <ReactDatePicker
          autoComplete="off"
          selectsRange
          startDate={startDate}
          endDate={endDate}
          placeholderText={datePlaceholder}
          onChange={(update: [Date, Date]) => {
            setUserHasSelectedDateRange(true);
            setDateRange(update);
          }}
          popperPlacement="bottom-end"
          isClearable
          //next line is a workaround to react date picker bug
          open={startDate !== null && endDate === null ? true : undefined}
          shouldCloseOnSelect={true}
          customInput={<input style={{ height: '100%' }} />}
        />
        {showLoader ? (
          <span className="css-loader--date-range-picker-loader"></span>
        ) : (
          <i className="icon-close" onClick={handleRemove} />
        )}
      </div>
    </div>
  );
};
export default memo(PeriodAndCustomDateRangeFilters);
